// src/components/LoginPage.js
import React, { useState } from "react";
import apiService from "../services/ApiService";
import {
  Box,
  Link,
  VStack,
  HStack,
  Heading,
  Image,
  Checkbox,
  Grid,
  GridItem,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Input,
  Button,
  Text,
} from "@chakra-ui/react";
import { useToast } from "@chakra-ui/react";
import { useNavigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import FormInput from "./FormInput";
import { faKey, faUser } from "@fortawesome/free-solid-svg-icons";
import * as IconButton from "../themes/IconButtonSet";
import { useAuth } from "../context/AuthContext";

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const titleFontSize = "clamp(0.7rem, 1.8vw, 0.9rem)";
  const bodyFontSize = "clamp(0.6rem, 1.8vw, 0.8rem)";
  const smFontSize = "clamp(0.5rem, 1.8vw, 0.7rem)";
  const toast = useToast();
  const { login } = useAuth();
  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);

  const { isOpen: isForgotOpen, onOpen: onForgotOpen, onClose: onForgotClose } = useDisclosure();
  const [forgotStep, setForgotStep] = useState(1);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotOtp, setForgotOtp] = useState("");
  const [forgotNewPassword, setForgotNewPassword] = useState("");
  const [forgotConfirmPassword, setForgotConfirmPassword] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);

  const base64_encode = (str) => btoa(unescape(encodeURIComponent(str)));
  const base64_decode = (str) => decodeURIComponent(escape(atob(str)));

  const encLoginData = (user, password) => {
    const loginValue = { user, password };
    const pos = Math.floor(Math.random() * 6) + 3;
    const schar = Math.floor(Math.random() * 9);
    const encoded = base64_encode(JSON.stringify(loginValue));
    return encoded.slice(0, pos) + schar + encoded.slice(pos) + pos;
  };

  const decLoginData = (token) => {
    try {
      const [header, payload] = token.split(".");
      if (!payload) throw new Error("Invalid JWT token");
      const decodedPayload = base64_decode(payload);
      return JSON.parse(decodedPayload);
    } catch (error) {
      console.error("Token decode error:", error);
      throw new Error("Failed to decode token");
    }
  };

  const handleLogin = async () => {
    if (!userName || !password) {
      toast({
        title: "กรุณากรอกชื่อผู้ใช้และรหัสผ่านให้ครบถ้วน",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (!isTermsAccepted) {
      toast({
        title: "กรุณายอมรับเงื่อนไขการใช้งานก่อน",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      const apiPath = "/getUserLoginInfo";
      const postData = { userauthen: encLoginData(userName, password) };

      //console.log("LOGIN DATA:", JSON.stringify(postData));
      const response = await apiService({
        method: "POST",
        url: apiPath,
        data: postData,
        sessionId: "",
      });
      //console.log("API Response:", JSON.stringify(response));

      if (response.result === "success") {
        login({
          sessionId: response.token,
          login: true,
          profile: response.Profile?.[0] || {},
          auth: response.Authorization?.[0] || {},
        });

        toast({
          title: "Successfully logged in!",
          status: "success",
          duration: 9000,
          isClosable: true,
        });
        //console.log("User Profile:", JSON.stringify(response.Profile[0]));
        if (response.Profile[0].OfficeID) {
          navigate("/dashboard");
        } else {
          navigate("/home");
        }
      } else {
        console.log("Login Failed with response:", JSON.stringify(response));
        toast({
          title: response.error || "Login failed",
          description: "กรุณาลองใหม่อีกครั้ง",
          status: "error",
          duration: 9000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Login Error:", error);
      toast({
        title: "Login failed: " + (error.message || "Unknown error"),
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    }
  };

  const handleForgotOpen = () => {
    setForgotStep(1);
    setForgotEmail("");
    setForgotOtp("");
    setForgotNewPassword("");
    setForgotConfirmPassword("");
    onForgotOpen();
  };

  useEffect(() => {
    if (location.state?.openForgotPassword) {
      handleForgotOpen();
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state]);

  const handleRequestOTP = async () => {
    const email = forgotEmail.trim();
    if (!email) {
      toast({ title: "กรุณากรอกอีเมล", status: "warning", duration: 4000, isClosable: true });
      return;
    }
    setForgotLoading(true);
    try {
      const res = await apiService({
        method: "POST",
        url: "/requestPasswordResetOTP",
        data: { email },
        sessionId: "",
      });
      if (res.result === "success") {
        toast({ title: res.message || "ส่ง OTP ไปที่อีเมลแล้ว", status: "success", duration: 6000, isClosable: true });
        setForgotStep(2);
      } else {
        toast({ title: res.error || "ส่ง OTP ไม่สำเร็จ", status: "error", duration: 5000, isClosable: true });
      }
    } catch (err) {
      toast({ title: err.message || "เกิดข้อผิดพลาด", status: "error", duration: 5000, isClosable: true });
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!forgotOtp.trim()) {
      toast({ title: "กรุณากรอกรหัส OTP", status: "warning", duration: 4000, isClosable: true });
      return;
    }
    if (!forgotNewPassword || forgotNewPassword.length < 6) {
      toast({ title: "รหัสผ่านใหม่ต้องมีอย่างน้อย 6 ตัวอักษร", status: "warning", duration: 4000, isClosable: true });
      return;
    }
    if (forgotNewPassword !== forgotConfirmPassword) {
      toast({ title: "รหัสผ่านใหม่กับยืนยันรหัสผ่านไม่ตรงกัน", status: "warning", duration: 4000, isClosable: true });
      return;
    }
    setForgotLoading(true);
    try {
      const res = await apiService({
        method: "POST",
        url: "/resetPasswordWithOTP",
        data: {
          email: forgotEmail.trim().toLowerCase(),
          otp: forgotOtp.trim(),
          newPassword: forgotNewPassword,
        },
        sessionId: "",
      });
      if (res.result === "success") {
        toast({ title: res.message || "ตั้งรหัสผ่านใหม่สำเร็จ", status: "success", duration: 5000, isClosable: true });
        onForgotClose();
      } else {
        toast({ title: res.error || "ตั้งรหัสผ่านไม่สำเร็จ", status: "error", duration: 5000, isClosable: true });
      }
    } catch (err) {
      toast({ title: err.message || "เกิดข้อผิดพลาด", status: "error", duration: 5000, isClosable: true });
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <Box
      maxW={{ base: "100%", md: "xl", lg: "4xl" }}
      mt={{ base: 15, md: 10, lg: 35 }}
      mb={{ base: 15, md: 10, lg: 30 }}
      mx="auto"
      borderWidth={1}
      borderRadius="2xl"
      boxShadow="lg"
      h="max-content"
      overflow="hidden"
    >
      <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}>
        <GridItem
          display={{ base: "none", md: "block" }}
          backgroundImage="url('/bglogin.png')"
          backgroundSize="cover"
          backgroundPosition="center"
        />
        <GridItem
          bg="white"
          p={{ base: 4, md: 6 }}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <VStack
            spacing={{ base: 1, md: 3 }}
            w={{ base: "100%", md: "80%" }}
            maxW="400px"
          >
            <VStack spacing={{ base: 3, md: 5 }} align="center">
              <Image
                src="/logo.png"
                alt="Logo"
                boxSize={{ base: "40px", md: "50px" }}
              />
              <VStack spacing={1} align="center">
                <Heading
                  fontSize={titleFontSize}
                  fontWeight="semibold"
                  color="#3b7072"
                  textAlign="center"
                >
                  สำนักเทคโนโลยีชีวภัณฑ์สัตว์ กรมปศุสัตว์
                </Heading>
                <Heading
                  fontSize={titleFontSize}
                  fontWeight="semibold"
                  color="#3b7072"
                  textAlign="center"
                >
                  Bureau of Veterinary Biologics
                </Heading>
              </VStack>
            </VStack>
            <VStack spacing={{ base: 1, md: 3 }} w="100%">
              <FormInput
                size="sm"
                fontSize={bodyFontSize}
                label="ชื่อผู้ใช้งาน"
                type="text"
                placeholder=""
                name="username"
                icon={faUser}
                value={userName}
                colorScheme="iBlue"
                onChange={(e) => setUserName(e.target.value)}
              />
              <FormInput
                size="sm"
                fontSize={bodyFontSize}
                label="รหัสผ่าน"
                type="password"
                placeholder=""
                name="password"
                icon={faKey}
                value={password}
                colorScheme="iBlue"
                onChange={(e) => setPassword(e.target.value)}
              />
              <Box mt={{ base: 2, md: 3 }} mb={{ base: 2, md: 3 }}>
                <Checkbox
                  isChecked={isTermsAccepted}
                  onChange={(e) => setIsTermsAccepted(e.target.checked)}
                  size={{ base: "sm", md: "md" }}
                >
                  <Box fontSize={smFontSize}>
                    ยอมรับเงื่อนไขการใช้งาน รวมถึงนโยบายความเป็นส่วนตัว
                    และยอมรับการใช้งานของคุกกี้ และเทคโนโลยีต่างๆ ที่เกี่ยวข้อง
                  </Box>
                </Checkbox>
              </Box>
              <IconButton.Login onClick={handleLogin} w="100%" size="sm" />
              
              <Link
                href="https://forms.gle/3vLA5WrQrYetRDoDA"
                target="_blank"
                color="teal.500"
                fontSize={bodyFontSize}
              >
              <Image
                src="banner/VHServiceQuestionair.png"
                alt="BannerQuestionair"
                w="100%"
                h="100%"
                objectFit="cover"
                objectPosition="center"
                borderRadius="lg"
                boxShadow="lg"
                mb={4}
              />
              </Link>
              
              <Link
                as="button"
                type="button"
                color="teal.500"
                fontSize={bodyFontSize}
                onClick={handleForgotOpen}
                _hover={{ textDecoration: "underline" }}
              >
                ลืมรหัสผ่าน
              </Link>
              <Link
                href="/registrationfarmer"
                color="teal.500"
                fontSize={bodyFontSize}
              >
                ลงทะเบียนเพื่อใช้งานระบบ
              </Link>
              <Link
                href="/videos/VaccineReservation.mp4"
                target="_blank"
                color="teal.500"
                fontSize={bodyFontSize}
              >
                วิดีโอสาธิตการใช้งานระบบสั่งจองวัคซีนออนไลน์
              </Link>
            </VStack>
          </VStack>
        </GridItem>
      </Grid>

      <Modal isOpen={isForgotOpen} onClose={onForgotClose} isCentered size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>ลืมรหัสผ่าน</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {forgotStep === 1 && (
              <VStack spacing={4} align="stretch">
                <Text color="gray.600" fontSize="sm">
                  กรอกอีเมลที่ใช้ลงทะเบียน ระบบจะส่งรหัส OTP ไปให้คุณ (รหัสมีอายุ 10 นาที)
                </Text>
                <FormControl>
                  <FormLabel>อีเมล</FormLabel>
                  <Input
                    type="email"
                    placeholder="example@email.com"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                  />
                </FormControl>
              </VStack>
            )}
            {forgotStep === 2 && (
              <VStack spacing={4} align="stretch">
                <Text color="gray.600" fontSize="sm">
                  กรอกรหัส OTP ที่ส่งไปที่ <strong>{forgotEmail}</strong> และตั้งรหัสผ่านใหม่
                </Text>
                <FormControl>
                  <FormLabel>รหัส OTP</FormLabel>
                  <Input
                    placeholder="กรอกรหัส 6 หลัก"
                    value={forgotOtp}
                    onChange={(e) => setForgotOtp(e.target.value)}
                    maxLength={6}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>รหัสผ่านใหม่</FormLabel>
                  <Input
                    type="password"
                    placeholder="อย่างน้อย 6 ตัวอักษร"
                    value={forgotNewPassword}
                    onChange={(e) => setForgotNewPassword(e.target.value)}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>ยืนยันรหัสผ่านใหม่</FormLabel>
                  <Input
                    type="password"
                    placeholder="กรอกรหัสผ่านอีกครั้ง"
                    value={forgotConfirmPassword}
                    onChange={(e) => setForgotConfirmPassword(e.target.value)}
                  />
                </FormControl>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            {forgotStep === 1 && (
              <>
                <Button variant="ghost" mr={3} onClick={onForgotClose}>
                  ยกเลิก
                </Button>
                <Button colorScheme="teal" onClick={handleRequestOTP} isLoading={forgotLoading} loadingText="กำลังส่ง...">
                  ส่ง OTP ไปที่อีเมล
                </Button>
              </>
            )}
            {forgotStep === 2 && (
              <>
                <Button variant="ghost" mr={3} onClick={() => setForgotStep(1)}>
                  ย้อนกลับ
                </Button>
                <Button colorScheme="teal" onClick={handleResetPassword} isLoading={forgotLoading} loadingText="กำลังบันทึก...">
                  ตั้งรหัสผ่านใหม่
                </Button>
              </>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default LoginPage;
